import { FC, useState, useEffect, ReactNode } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  IconButton,
  Typography,
  Collapse,
  Fade,
  Grow,
} from '@mui/material';
import {
  ChevronLeft,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { menuItems } from '../config/menuItems';
import { filterMenuItems } from '../utils/filterMenuItems';

// Types
interface SubMenuItem {
  title: string;
  text: string;
  route: string;
}

interface MenuItem {
  title: string;
  text: string;
  icon?: any;
  route?: string;
  children?: SubMenuItem[];
}

interface NestedDrawerMenuProps {
  open: boolean;
  onDrawerToggle: () => void;
}

interface NestedMenuItemProps {
  item: MenuItem | SubMenuItem;
  level?: number;
  onClick?: () => void;
  isOpen?: boolean;
  onToggle?: () => void;
}

// Icon kontrolü için type guard fonksiyonu
const isMenuItem = (item: MenuItem | SubMenuItem): item is MenuItem => {
  return 'icon' in item;
};

const NestedMenuItem: FC<NestedMenuItemProps> = ({
  item,
  level = 0,
  onClick,
  isOpen = false,
  onToggle
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isSelected = 'route' in item && item.route
    ? location.pathname === item.route
    : false;
  const hasChildren = 'children' in item && Array.isArray(item.children);

  const handleClick = () => {
    if (hasChildren && level === 0) {
      onToggle?.(); // Ana menü için accordion toggle
    } else if ('route' in item && item.route) {
      navigate(item.route);
      if (!level) {
        onClick?.();
      }
    }
  };

  return (
    <>
      <Grow in={true} timeout={300 + (level * 100)}>
        <ListItem
          disablePadding
          sx={{
            display: 'block',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <ListItemButton
            onClick={handleClick}
            selected={isSelected}
            sx={{
              minHeight: level > 0 ? 25 : 44, // daha kompakt
              pl: level > 0 ? 3.5 : 2,
              color: isSelected
                ? level > 0
                  ? '#1e3c72'
                  : '#ffffff'
                : (level > 0 ? '#64748b' : '#374151'),
              backgroundColor: isSelected
                ? level > 0
                  ? 'rgba(46, 82, 152, 0.10)'
                  : 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)'
                : 'transparent',
              borderRadius: level > 0 ? '0' : '10px',
              margin: level > 0 ? '0' : '3px 8px',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              boxShadow: isSelected
                ? level > 0
                  ? 'inset 0 2px 8px rgba(46, 82, 152, 0.10), 0 1px 3px rgba(46, 82, 152, 0.06)'
                  : '0 4px 16px rgba(30, 60, 114, 0.18), inset 0 1px 0 rgba(255,255,255,0.08)'
                : 'none',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: level > 0 ? 0 : '-2px',
                width: level > 0 ? '2px' : '2px',
                height: '100%',
                background: isSelected
                  ? level > 0
                    ? 'linear-gradient(180deg, #2a5298 0%, #1e3c72 100%)'
                    : 'linear-gradient(180deg, #60a5fa 0%, #2a5298 100%)'
                  : 'transparent',
                transition: 'all 0.3s ease',
                borderRadius: '0 2px 2px 0',
              },
              '&:hover': {
                backgroundColor: isSelected
                  ? level > 0
                    ? 'rgba(46, 82, 152, 0.18)'
                    : 'linear-gradient(135deg, #1a365d 0%, #2c5282 100%)'
                  : level > 0
                    ? 'rgba(46, 82, 152, 0.09)'
                    : 'rgba(30, 60, 114, 0.10)',
                color: isSelected
                  ? level > 0
                    ? '#1e3c72'
                    : '#ffffff'
                  : (level > 0 ? '#2a5298' : '#1e3c72'),
                transform: level > 0 ? 'translateX(6px)' : 'translateY(-1px)',
                boxShadow: isSelected
                  ? level > 0
                    ? 'inset 0 0 18px rgba(46, 82, 152, 0.13), 0 2px 8px rgba(46, 82, 152, 0.10)'
                    : '0 8px 24px rgba(26, 54, 93, 0.18), inset 0 1px 0 rgba(255,255,255,0.10)'
                  : level > 0
                    ? '0 2px 8px rgba(46, 82, 152, 0.10)'
                    : '0 2px 10px rgba(30, 60, 114, 0.10)',
                '& .menu-icon': {
                  transform: 'scale(1.04)',
                  filter: 'brightness(1.08)',
                },
                '& .expand-icon': {
                  transform: 'rotate(180deg) scale(1.08)',
                },
              },
              '&:active': {
                transform: level > 0
                  ? 'translateX(4px) scale(0.98)'
                  : 'translateY(0px) scale(0.97)',
                transition: 'all 0.1s ease',
              },
            }}
          >
            {isMenuItem(item) && item.icon && (
              <ListItemIcon
                className="menu-icon"
                sx={{
                  minWidth: 36,
                  color: isSelected
                    ? level > 0
                      ? '#1e3c72'
                      : '#ffffff'
                    : (level > 0 ? '#64748b' : '#6b7280'),
                  transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  opacity: isSelected ? 1 : (level > 0 ? 0.85 : 0.9),
                  '.MuiListItemButton-root:hover &': {
                    color: isSelected
                      ? level > 0
                        ? '#1e3c72'
                        : '#ffffff'
                      : (level > 0 ? '#2a5298' : '#1e3c72'),
                  },
                }}
              >
                <item.icon />
              </ListItemIcon>
            )}
            <ListItemText
              primary={
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: level === 0 ? 700 : 500,
                    fontSize: level === 0 ? '0.88rem' : '0.74rem', // font-size küçültüldü
                    color: 'inherit',
                    transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    opacity: level > 0 ? 0.95 : 1,
                    letterSpacing: level === 0 ? '0.2px' : '0.4px',
                    textShadow: isSelected && level === 0
                      ? '0 1px 2px rgba(0,0,0,0.08)'
                      : 'none',
                  }}
                >
                  {item.text}
                </Typography>
              }
            />
            {hasChildren && (
              <Box
                component="span"
                className="expand-icon"
                sx={{
                  color: 'inherit',
                  transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  display: 'flex',
                  alignItems: 'center',
                  opacity: 0.8,
                  '&:hover': {
                    opacity: 1,
                  },
                }}
              >
                {isOpen ? <ExpandLess /> : <ExpandMore />}
              </Box>
            )}
          </ListItemButton>
        </ListItem>
      </Grow>
      {hasChildren && (
        <Collapse
          in={isOpen}
          timeout={400}
          unmountOnExit
          sx={{
            '& .MuiCollapse-wrapper': {
              transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            },
            '& .MuiCollapse-wrapperInner': {
              paddingLeft: 0,
              borderLeft: '1px solid rgba(46, 82, 152, 0.12)',
              marginLeft: '24px',
              background: 'linear-gradient(180deg, rgba(46, 82, 152, 0.03) 0%, transparent 100%)',
            }
          }}
        >
          <List component="div" disablePadding sx={{ position: 'relative' }}>
            {(item as MenuItem).children?.map((child, index) => (
              <Fade
                in={isOpen}
                timeout={300 + (index * 75)}
                key={child.title}
                style={{ transformOrigin: 'left center' }}
              >
                <div>
                  <NestedMenuItem
                    item={child}
                    level={level + 1}
                    onClick={onClick}
                    isOpen={false}
                  />
                </div>
              </Fade>
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

const NestedDrawerMenu: FC<NestedDrawerMenuProps> = ({ open, onDrawerToggle }) => {
  const navigate = useNavigate();
  const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({});

  // Redux state'den currentUserState'i al
  const { currentUserState } = useSelector((state: RootState) => state.currentUserState);

  // isUsedMealCard değerine göre menüleri filtrele
  const filteredMenuItems = filterMenuItems(menuItems, currentUserState?.isUsedMealCard ?? null);

  const handleMenuToggle = (menuTitle: string) => {
    setOpenMenus(prev => ({
      ...Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {}), // Tüm menüleri kapat
      [menuTitle]: !prev[menuTitle] // Sadece tıklanan menüyü aç/kapat
    }));
  };

  return (
    <>
      <Box
        sx={{
          padding: "4px 0px",
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/assets/images/logo/sidenavlogo.png`}
          alt="Logo"
          onClick={() => navigate('/dashboard/transactions')}
          sx={{
            height: 33,
            margin: '0 0 0 25px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'scale(1.05)',
              filter: 'brightness(1.1)',
            }
          }}
        />
        <IconButton
          onClick={onDrawerToggle}
          sx={{
            marginRight: 1,
            color: '#666',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              color: '#00aafe',
              backgroundColor: 'rgba(0, 170, 254, 0.1)',
              transform: 'rotate(-180deg)',
            }
          }}
        >
          <ChevronLeft />
        </IconButton>
      </Box>
      <List
        sx={{
          pt: 1,
          background: 'linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)',
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        {filteredMenuItems.map((item, index) => (
          <Fade in={true} timeout={300 + (index * 100)} key={item.title}>
            <div>
              <NestedMenuItem
                item={item}
                onClick={onDrawerToggle}
                level={0}
                isOpen={openMenus[item.title] || false}
                onToggle={() => handleMenuToggle(item.title)}
              />
            </div>
          </Fade>
        ))}
      </List>
    </>
  );
};

export default NestedDrawerMenu;